import { useFormik } from "formik"
import React, { useEffect, useState } from "react"
import { Col, FloatingLabel, Form, Row, Spinner } from "react-bootstrap"
import * as Yup from "yup"
import { toast } from "react-toastify"

import CustomSelect from "../../../components/CustomSelect"
import ModalComponent from "../../../components/Modal"

import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import { handleOnBlur, handleOnchange } from "../../../lib/GlobalMethods"

const AddEditFleetClient = ({
  isUpdate,
  setIsUpdate,
  show,
  setShow,
  fetchData,
  handleClose,
}) => {
  const [loading, setLoading] = useState(false)
  const [stationData, setStationData] = useState([])

  const statusOptions = [
    {
      value: "ACTIVE",
      label: "Active",
    },
    {
      value: "INACTIVE",
      label: "Inactive",
    },
  ]

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(2, "Enter at least 2 characters for Fleet Client Name")
      .required("Fleet Client Name is required"),

    status: Yup.string().required("Status is required"),

    station_ids: Yup.array(),
  })

const fetchStationData = async () => {
  try {
    const response = await axiosProvider({
      method: "GET",
      endpoint: admin.chargingStationList,
    })

    if (response?.data?.code === 200) {
      const unassignedStations = response?.data?.data || []

      // Current fleet client's already assigned stations
      const assignedStations = isUpdate?.stations || []

      // Combine unassigned stations + current client's stations
      const allStations = [...unassignedStations, ...assignedStations]

      // Remove duplicates
      const uniqueStations = Array.from(
        new Map(allStations.map((station) => [station.id, station])).values(),
      )

      const formattedStations = uniqueStations.map((station) => ({
        value: station?.id,
        label: station?.station_name,
      }))

      setStationData(formattedStations)
    }
  } catch (error) {
    toast.error(error?.message || "Unable to fetch charging stations")
  }
}
  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true)

      const payload = {
        name: values?.name?.trim(),
        status: values?.status || "ACTIVE",
        station_ids: values?.station_ids || [],
      }

      const response = await axiosProvider({
        method: isUpdate ? "PATCH" : "POST",
        endpoint: isUpdate
          ? admin.updateFleetClient + isUpdate.id
          : admin.createFleetClient,
        data: payload,
      })

      if (response?.data?.code === 200 || response?.data?.code === 201) {
        toast.success(response?.data?.message)

        setShow(false)
        setIsUpdate(false)

        resetForm()

        fetchData()
      } else {
        throw new Error(
          response?.data?.message || "Unable to save Fleet Client",
        )
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong")
    } finally {
      setLoading(false)
    }
  }

  const formik = useFormik({
    initialValues: isUpdate
      ? {
          name: isUpdate?.name || "",
          status: isUpdate?.status || "ACTIVE",
          station_ids: isUpdate?.stations?.map((station) => station?.id) || [],
        }
      : {
          name: "",
          status: "ACTIVE",
          station_ids: [],
        },

    validationSchema,

    onSubmit: handleSubmit,

    enableReinitialize: true,
  })

  const { values, errors, touched, setFieldValue, resetForm } = formik

  useEffect(() => {
    if (show) {
      fetchStationData()
    }
  }, [show])

  return (
    <ModalComponent
      show={show}
      modalsize="md"
      backdrop="static"
      animationFade={true}
      onHide={() => {
        handleClose()
        resetForm()
      }}
      loading={loading}
      modeltitle={isUpdate ? "Update Fleet Client" : "Add Fleet Client"}
      submitname={
        loading ? (
          <Spinner size="sm" animation="border" variant="white" />
        ) : isUpdate ? (
          "Update"
        ) : (
          "Add"
        )
      }
      btnclosetext="Cancel"
      handleSubmit={formik.handleSubmit}
      children={
        <Form noValidate>
          <Row xs="auto">
            <Col xs={12}>
              <FloatingLabel
                controlId="fleetClientName"
                label={
                  <>
                    Fleet Client Name <span className="required_star">*</span>
                  </>
                }
                className="mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder=""
                  name="name"
                  autoComplete="false"
                  maxLength={100}
                  value={values?.name}
                  onChange={(e) => handleOnchange(e, false, formik)}
                  onBlur={(e) => handleOnBlur(e, formik)}
                  isInvalid={touched?.name && errors?.name}
                  isValid={touched?.name && !errors?.name}
                />

                <Form.Control.Feedback type="invalid">
                  {errors?.name}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col xs={12} className="mb-3 custom_selectinvalid_bdr">
              <label>
                Status <span className="required_star">*</span>
              </label>

              <CustomSelect
                value={
                  statusOptions.find(
                    (status) => status.value === values?.status,
                  ) || null
                }
                handleSelectChange={(selectedOption) => {
                  setFieldValue("status", selectedOption?.value || "ACTIVE")
                }}
                options={statusOptions}
                placeholder="Select Status"
              />

              {touched?.status && errors?.status && (
                <p className="invalid_error">{errors.status}</p>
              )}
            </Col>

            <Col xs={12} className="mb-3 custom_selectinvalid_bdr">
              <label>Charging Stations</label>

              <CustomSelect
                value={stationData.filter((station) =>
                  values?.station_ids?.includes(station?.value),
                )}
                handleSelectChange={(selectedOptions) => {
                  setFieldValue(
                    "station_ids",
                    selectedOptions
                      ? selectedOptions.map((option) => option?.value)
                      : [],
                  )
                }}
                options={stationData}
                isMulti={true}
                placeholder="Select Charging Stations"
              />

              {touched?.station_ids && errors?.station_ids && (
                <p className="invalid_error">{errors.station_ids}</p>
              )}
            </Col>
          </Row>
        </Form>
      }
    />
  )
}

export default AddEditFleetClient
